import { 
  auth, 
  db, 
  doc, 
  getDoc, 
  updateDoc 
} from '@/firebase'
import { logDebug, logError, logSuccess } from '@/utils/secureLogger'

export interface GoogleCalendarTokens {
  access_token: string
  refresh_token: string
  expiry_date: number
  scope: string
  token_type: string
}

/**
 * Exchange authorization code for Google Calendar tokens
 */
export async function exchangeCodeForTokens(code: string): Promise<GoogleCalendarTokens> {
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID
  const GOOGLE_CLIENT_SECRET = import.meta.env.VITE_GOOGLE_CLIENT_SECRET
  const REDIRECT_URI = `${window.location.origin}/oauth-callback`

  try {
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code,
        client_id: GOOGLE_CLIENT_ID,
        client_secret: GOOGLE_CLIENT_SECRET,
        redirect_uri: REDIRECT_URI,
        grant_type: 'authorization_code'
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(`Token exchange failed: ${errorData.error_description || 'Unknown error'}`)
    }

    const tokens = await response.json()
    
    return {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      expiry_date: Date.now() + (tokens.expires_in * 1000),
      scope: tokens.scope,
      token_type: tokens.token_type
    }
  } catch (error) {
    logError('Error exchanging code for tokens', error)
    throw error
  }
}

/**
 * Store Google Calendar tokens in Firestore
 */
export async function storeGoogleCalendarTokens(
  userId: string, 
  tokens: GoogleCalendarTokens
): Promise<void> {
  try {
    await updateDoc(doc(db, 'users', userId), {
      googleCalendar: tokens
    })
    logSuccess('Google Calendar tokens stored successfully')
  } catch (error) {
    logError('Error storing Google Calendar tokens', error)
    throw error
  }
}

/**
 * Get Google Calendar tokens for user
 */
export async function getGoogleCalendarTokens(userId: string): Promise<GoogleCalendarTokens | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId))
    if (userDoc.exists()) {
      const userData = userDoc.data()
      return userData.googleCalendar || null
    }
    return null
  } catch (error) {
    logError('Error getting Google Calendar tokens', error)
    return null
  }
}

/**
 * Clear Google Calendar tokens from Firestore (when invalid or user disconnects)
 */
export async function clearGoogleCalendarTokens(userId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'users', userId), {
      googleCalendar: null,
      googleCalendarConnected: false
    })
    logDebug('Google Calendar tokens cleared')
  } catch (error) {
    logError('Error clearing Google Calendar tokens', error)
  }
}

/**
 * Check if user has Google Calendar connected
 */
export async function isGoogleCalendarConnected(userId: string): Promise<boolean> {
  try {
    const tokens = await getGoogleCalendarTokens(userId)
    return !!(tokens?.access_token)
  } catch (error) {
    logError('Error checking Google Calendar connection', error)
    return false
  }
}

/**
 * Remove Google Calendar connection
 */
export async function removeGoogleCalendarConnection(userId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'users', userId), {
      googleCalendar: null
    })
    logSuccess('Google Calendar connection removed successfully')
  } catch (error) {
    logError('Error removing Google Calendar connection', error)
    throw error
  }
}

/**
 * Refresh Google Calendar access token
 */
export async function refreshGoogleCalendarToken(userId: string): Promise<string | null> {
  try {
    const tokens = await getGoogleCalendarTokens(userId)
    if (!tokens?.refresh_token) {
      throw new Error('No refresh token available')
    }

    const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID
    const GOOGLE_CLIENT_SECRET = import.meta.env.VITE_GOOGLE_CLIENT_SECRET

    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        refresh_token: tokens.refresh_token,
        client_id: GOOGLE_CLIENT_ID,
        client_secret: GOOGLE_CLIENT_SECRET,
        grant_type: 'refresh_token'
      })
    })

    if (!response.ok) {
      // Token refresh failed - likely invalid or expired refresh token
      // Clear the invalid tokens from Firestore so user can reconnect
      logError('Failed to refresh Google Calendar token - clearing invalid tokens', new Error(`HTTP ${response.status}`))
      await clearGoogleCalendarTokens(userId)
      throw new Error('Failed to refresh token')
    }

    const newTokens = await response.json()
    const updatedTokens: GoogleCalendarTokens = {
      ...tokens,
      access_token: newTokens.access_token,
      expiry_date: Date.now() + (newTokens.expires_in * 1000)
    }

    await storeGoogleCalendarTokens(userId, updatedTokens)
    return newTokens.access_token
  } catch (error) {
    logError('Error refreshing Google Calendar token', error)
    // Clear tokens on any error to allow user to reconnect
    await clearGoogleCalendarTokens(userId)
    return null
  }
}


/**
 * Create a Google Calendar event
 */
export async function createGoogleCalendarEvent(
  userId: string,
  event: {
    summary: string
    description: string
    start: { dateTime: string; timeZone?: string }
    end: { dateTime: string; timeZone?: string }
    conferenceData?: { createRequest: { requestId: string } }
  }
): Promise<any> {
  try {
    const tokens = await getGoogleCalendarTokens(userId)
    if (!tokens) {
      throw new Error('Google Calendar not connected')
    }

    // Check if token needs refresh
    const now = Date.now()
    let accessToken = tokens.access_token
    
    if (tokens.expiry_date && now >= tokens.expiry_date) {
      const newAccessToken = await refreshGoogleCalendarToken(userId)
      if (!newAccessToken) {
        throw new Error('Failed to refresh access token')
      }
      accessToken = newAccessToken
    }

    // Create the calendar event
    const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events?conferenceDataVersion=1', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event)
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(`Failed to create calendar event: ${errorData.error?.message || 'Unknown error'}`)
    }

    return await response.json()
  } catch (error) {
    logError('Error creating Google Calendar event', error)
    throw error
  }
}

/**
 * Create a tutoring session event in Google Calendar
 */
export async function createTutoringSessionEvent(
  userId: string,
  sessionData: {
    studentName: string
    subject: string
    startTime: string
    endTime: string
    type: 'online' | 'face-to-face'
    location?: string
    notes?: string
  }
): Promise<any> {
  const event: any = {
    summary: `Tutoring Session - ${sessionData.subject}`,
    description: `Tutoring session with ${sessionData.studentName}${sessionData.notes ? `\n\nNotes: ${sessionData.notes}` : ''}`,
    start: {
      dateTime: sessionData.startTime,
      timeZone: 'Asia/Manila' // Adjust based on your timezone
    },
    end: {
      dateTime: sessionData.endTime,
      timeZone: 'Asia/Manila'
    }
  }

  // Add Google Meet link for online sessions
  if (sessionData.type === 'online') {
    event.conferenceData = {
      createRequest: {
        requestId: `tutoring-session-${Date.now()}`
      }
    }
  }

  const calendarEvent = await createGoogleCalendarEvent(userId, event)
  
  // Return both the event data and the event ID for future deletion
  return {
    ...calendarEvent,
    eventId: calendarEvent.id
  }
}

/**
 * Delete a Google Calendar event
 */
export async function deleteGoogleCalendarEvent(
  userId: string,
  eventId: string
): Promise<boolean> {
  try {
    const tokens = await getGoogleCalendarTokens(userId)
    if (!tokens) {
      throw new Error('Google Calendar not connected')
    }

    // Check if token needs refresh
    const now = Date.now()
    let accessToken = tokens.access_token
    
    if (tokens.expiry_date && now >= tokens.expiry_date) {
      const newAccessToken = await refreshGoogleCalendarToken(userId)
      if (!newAccessToken) {
        throw new Error('Failed to refresh access token')
      }
      accessToken = newAccessToken
    }

    // Delete the calendar event
    const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(`Failed to delete calendar event: ${errorData.error?.message || 'Unknown error'}`)
    }

    logSuccess('Google Calendar event deleted successfully')
    return true
  } catch (error) {
    logError('Error deleting Google Calendar event', error)
    throw error
  }
}
